import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, DatePicker, Form, Input, Select, Space } from 'antd';
import StandardTable from '../../../../../components/common/StandardTable';
import dayjs, { Dayjs } from 'dayjs';
import { fetchSqlDataWithQuery } from '../../../../../services/api';
import type { VwQaqcPipeReportBc } from '../../../../../types';
import Button from '../../../../../components/common/Button';

const { RangePicker } = DatePicker;

const QaqcPipeNdtReports: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<VwQaqcPipeReportBc[]>([]);
  const [filteredRows, setFilteredRows] = useState<VwQaqcPipeReportBc[]>([]);

  const load = async () => {
    const v = form.getFieldsValue();
    const order = ' order by report_date desc, report_id, weldingdata_id, weld_no_rev';
    const sql = `select * from public.vw_qaqc_pipe_report_bc${order}`;

    setLoading(true);
    try {
      const res: any = await fetchSqlDataWithQuery(sql);
      const data: VwQaqcPipeReportBc[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setRows(data);
      setFilteredRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* initial */ }, []);

  // Client-side filtering: compute filteredRows on filter change
  const applyFilters = () => {
    const v = form.getFieldsValue();
    const contains = (a?: string, b?: string) =>
      (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(v.date_range) ? v.date_range : [];
    const filtered = rows.filter(r => {
      if (v.report_id && String(r.report_id) !== String(v.report_id)) return false;
      if (v.service_class_name && String(r.service_class_name) !== String(v.service_class_name)) return false;
      if (v.code_no && String(r.code_no) !== String(v.code_no)) return false;
      if (v.result && String(r.result ?? '') !== String(v.result)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    setFilteredRows(filtered);
  };
  const onValuesChange = () => { applyFilters(); };

  // Cascading options derived from rows filtered by other fields
  const serviceOptions = useMemo(() => {
    // Filter by all fields except service_class_name
    const v = form.getFieldsValue();
    const temp = { ...v, service_class_name: undefined };
    const backup = form.getFieldsValue;
    // Create a temporary filtering using current function
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(temp.date_range) ? temp.date_range : [];
    const pool = rows.filter(r => {
      if (temp.report_id && !contains(r.report_id, temp.report_id)) return false;
      if (temp.code_no && String(r.code_no) !== String(temp.code_no)) return false;
      if (temp.result && String(r.result ?? '') !== String(temp.result)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    const set = new Set<string>();
    pool.forEach(r => { if (r.service_class_name) set.add(String(r.service_class_name)); });
    return Array.from(set).sort().map(vv => ({ value: vv, label: vv }));
  }, [rows, form]);

  const reportIdOptions = useMemo(() => {
    // Derive distinct report_id from rows after applying other filters (except report_id)
    const v = form.getFieldsValue();
    const temp = { ...v, report_id: undefined };
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(temp.date_range) ? temp.date_range : [];
    const pool = rows.filter(r => {
      if (temp.service_class_name && String(r.service_class_name) !== String(temp.service_class_name)) return false;
      if (temp.code_no && String(r.code_no) !== String(temp.code_no)) return false;
      if (temp.result && String(r.result ?? '') !== String(temp.result)) return false;
      if (temp.report_id && !contains(r.report_id, temp.report_id)) return false; // not used currently, kept for future typing support
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    const set = new Set<string>();
    pool.forEach(r => { if (r.report_id) set.add(String(r.report_id)); });
    return Array.from(set).sort().map(vv => ({ value: vv, label: vv }));
  }, [rows, form]);
  const codeOptions = useMemo(() => {
    const v = form.getFieldsValue();
    const temp = { ...v, code_no: undefined };
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(temp.date_range) ? temp.date_range : [];
    const pool = rows.filter(r => {
      if (temp.report_id && !contains(r.report_id, temp.report_id)) return false;
      if (temp.service_class_name && String(r.service_class_name) !== String(temp.service_class_name)) return false;
      if (temp.result && String(r.result ?? '') !== String(temp.result)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    const set = new Set<string>();
    pool.forEach(r => { if (r.code_no) set.add(String(r.code_no)); });
    return Array.from(set).sort().map(vv => ({ value: vv, label: vv }));
  }, [rows, form]);

  const columns: any[] = useMemo(() => ([
    { title: 'Date', dataIndex: 'report_date', key: 'report_date', width: 110 },
    { title: 'Report ID', dataIndex: 'report_id', key: 'report_id', width: 140 },
    { title: 'Weld ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 220 },
    { title: 'Rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 60 },
    { title: 'Result', dataIndex: 'result', key: 'result', width: 90 },
    { title: 'Size', dataIndex: 'pipe_size', key: 'pipe_size', width: 70 },
    { title: 'SC', dataIndex: 'service_class_name', key: 'service_class_name', width: 70 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 70 },
    { title: 'Type', dataIndex: 'weldtype_name', key: 'weldtype_name', width: 70 },
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 80 },
    { title: 'F/S', dataIndex: 'weld_category', key: 'weld_category', width: 60 },
    { title: 'Code', dataIndex: 'code_no', key: 'code_no', width: 65 },
    { title: 'Heat#1', dataIndex: 'heat_no_1', key: 'heat_no_1', width: 100 },
    { title: 'Heat#2', dataIndex: 'heat_no_2', key: 'heat_no_2', width: 100 },
    { title: 'B.ang', dataIndex: 'bevel_angle', key: 'bevel_angle', width: 70 },
    { title: 'R.Face', dataIndex: 'root_face_mm', key: 'root_face_mm', width: 70 },
    { title: 'R.Gap', dataIndex: 'root_gap_mm', key: 'root_gap_mm', width: 70 },
    { title: 'Hi/Lo', dataIndex: 'hi_lo_mm', key: 'hi_lo_mm', width: 70 },
    { title: 'Oval.', dataIndex: 'ovality_ok', key: 'ovality_ok', width: 70, render: (v: any) => (v === true ? 'Yes' : v === false ? 'No' : '-') },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
  ]), []);

  return (
    <Card
      title="NDT Reports / Bevel Check"
      extra={<Button onClick={() => { form.resetFields(); setFilteredRows(rows); }}>Reset</Button>}
      style={{ maxWidth: '100%', margin: '0 auto', rowGap:2 }}
      headStyle={{ marginBottom: 4, padding: '8px 12px' }}
      bodyStyle={{ padding: 8, paddingTop: 0 }}
    >
      <Form
        form={form}
        layout="inline"
        initialValues={{ result: undefined, service_class_name: undefined, code_no: undefined, date_range: undefined }}
        onValuesChange={onValuesChange}
        style={{ marginBottom: 2, gap: 1, rowGap: 4 }}
      >
        <Form.Item name="date_range" style={{marginRight:6}}>
          <RangePicker
            allowEmpty={[true,true]}
            placeholder={["From date", "To date"]}
            style={{ width: 180 }}
          />
        </Form.Item>
        <Form.Item name="report_id" style={{marginRight:6}}>
          <Select
            allowClear
            showSearch
            placeholder="Report ID"
            style={{ width: 200 }}
            options={reportIdOptions}
            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="service_class_name" style={{marginRight:6}}>
          <Select
            allowClear
            showSearch
            placeholder="S.Class"
            style={{ width: 100 }}
            options={serviceOptions}
            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="code_no" style={{marginRight:6}}>
          <Select
            allowClear
            showSearch
            placeholder="Code"
            style={{ width: 80 }}
            options={codeOptions}
            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="result" style={{marginRight:6}}>
          <Select allowClear placeholder="Result" style={{ width: 100 }} options={[
            { value: 'PASS', label: 'PASS' },
            { value: 'FAIL', label: 'FAIL' },
            { value: 'REWORK', label: 'REWORK' },
          ]} />
        </Form.Item>
      </Form>

      <StandardTable
        loading={loading}
        rowKey={(r: VwQaqcPipeReportBc) => `${r.report_id}-${r.weldingdata_id}-${String(r.weld_no_rev ?? '')}`}
        dataSource={filteredRows}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
        bordered
      />
    </Card>
  );
};

export default QaqcPipeNdtReports;
